<template>
  <div class="select" tabindex="0" @focusout="handleFocusOut">
    <div
      class="select__selector"
      :class="{ 'select__selector--disabled': disabled }"
      @click="toggle()"
    >
      <img v-if="labelPrefix" :src="labelPrefix" class="select__prefix" />
      <div class="select__text" :class="{ 'select__text--placeholder': !value }">
        {{ displayValue }}
      </div>
      <UseSvg
        class="select__arrow"
        :class="{ 'select__arrow--expanded': visible }"
        icon="arrow-right"
      />
      <div class="select__menu" :class="{ hidden: !visible }">
        <ul class="select__list">
          <li
            v-for="item in options"
            :key="item.value"
            class="select__list-item"
            :class="{ 'select__list-item--current': isCurrentValue(item) }"
            @click.stop="select(item)"
          >
            <img v-if="item.prefix" :src="item.prefix" class="select__prefix" />
            {{ item.label }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script>
import UseSvg from "common/vue/components/UseSvg.vue";

export default {
  // eslint-disable-next-line vue/no-reserved-component-names
  name: "Select",
  components: { UseSvg },
  props: {
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    placeholder: {
      type: String,
      default: "Select an option"
    },
    value: {
      type: Object,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ["on-change"],
  data() {
    return {
      visible: false
    };
  },
  computed: {
    displayValue() {
      if (this.value) {
        return this.value.label;
      }
      return this.placeholder;
    },
    labelPrefix() {
      return this.value?.prefix;
    },
    disableOpen() {
      return this.disabled || this.options.length === 0;
    }
  },
  methods: {
    isCurrentValue({ value }) {
      return value === this.value?.value;
    },
    toggle() {
      if (this.disableOpen) {
        return;
      }
      this.visible = !this.visible;
    },
    select(option) {
      this.$emit("on-change", option.value);
      this.toggle();
    },
    handleFocusOut(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.visible = false;
      }
    }
  }
};
</script>
<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.select {
  font-size: 12px;
  font-family: $font-family-inter;
  outline: none;

  &__selector {
    border: 1px solid $gray-border;
    border-radius: 8px;
    padding: 10px 5px;
    font-family: inherit;
    font-size: inherit;
    font-weight: 600;
    position: relative;
    cursor: pointer;
    display: flex;
    align-items: center;
    height: 35px;

    &--disabled {
      background: $gray-border-lightest;
      color: $crafterpay-gray-modern-400;
      cursor: not-allowed;
    }
  }

  &__prefix {
    width: 24px;
    height: 24px;
    margin: 0 5px;
    border-radius: 50%;
  }

  &__arrow {
    position: absolute;
    right: 10px;
    top: 11px;
    width: 10px;
    height: 10px;
    transition-duration: 0.1s;
    transform: rotateZ(-90deg);
    color: $crafterpay-gray-modern-400;

    &--expanded {
      transform: rotateZ(90deg);
    }
  }

  &__text {
    font-size: inherit;
    line-height: 12px;
    align-self: flex-end;
    user-select: none;

    &--placeholder {
      font-weight: 500;
      color: $crafterpay-gray-modern-400;
    }
  }

  &__menu {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    transition-duration: 0.1s;
    z-index: 999;
    box-shadow:
      rgba(0, 0, 0, 0.1) 0px 10px 15px -3px,
      rgba(0, 0, 0, 0.05) 0px 4px 6px -2px;
    border-radius: 8px;
    margin: 3px 0 0 0;
    max-height: 300px;
    overflow-y: auto;

    @include with-tablet {
      max-height: 500px;
    }
  }

  &__list {
    width: 100%;
    list-style-type: none;
    font-size: inherit;
    border: 1px solid $gray-border;
    border-radius: 8px;
    background: $white;
  }

  &__list-item {
    height: 35px;
    padding: 10px 5px;
    color: $crafterpay-gray-modern-600;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:hover {
      color: white;
      background: $primary-light;
    }

    &:first-child {
      border-top-left-radius: 7px;
      border-top-right-radius: 7px;
    }

    &:last-child {
      border-bottom-left-radius: 7px;
      border-bottom-right-radius: 7px;
    }

    &--current {
      color: $black;
      background: $crafterpay-gray-modern-200;
    }
  }
}
</style>
